/**
 * LabourReportPanel — job-cost report for a date range, shared by the Hours
 * tab ("Labour by job" toggle) and the Search page.
 *
 * Pick a From/To range: every job with labour OR materials in that period is
 * listed with hours, gross labour pay (all employees combined), materials,
 * and a combined total. Clicking a job (or picking it from the dropdown)
 * drills into every itemized labour entry and material expense.
 *
 * With `showOverhead` (Search page), a card on top adds the OVERHEAD total:
 * gross hourly pay on rows not linked to a job (unassigned + Office +
 * Estimating) plus salaried staff prorated weekly_salary ÷ 7 × range days.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LABOUR_REPORT_ROLES = ["owner", "superadmin", "admin"];

function mondayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  return x;
}
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function LabourReportPanelInner({ showOverhead = false }) {
  const [repFrom, setRepFrom] = useState(() => toISO(addDays(mondayOf(new Date()), -7))); // last week's Monday
  const [repTo, setRepTo] = useState(() => toISO(addDays(mondayOf(new Date()), -1)));    // last week's Sunday
  const [repJob, setRepJob] = useState(""); // "" = every job with labour/materials in the range
  const [report, setReport] = useState(null);
  const [reportBusy, setReportBusy] = useState(false);
  const [repNonce, setRepNonce] = useState(0); // bumps when monthly overhead items change

  useEffect(() => {
    let alive = true;
    setReportBusy(true);
    api.get("/timesheets/labour-by-job", { params: { from: repFrom, to: repTo } })
      .then((r) => { if (alive) setReport(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load the labour report"))
      .finally(() => { if (alive) setReportBusy(false); });
    return () => { alive = false; };
  }, [repFrom, repTo, repNonce]);

  const repEntries = useMemo(
    () => (report && repJob ? report.entries.filter((e) => e.project_id === repJob) : []),
    [report, repJob]
  );
  const repJobRow = useMemo(() => (report?.jobs || []).find((j) => j.project_id === repJob), [report, repJob]);
  const repMats = useMemo(
    () => (report && repJob ? (report.materials || []).filter((m) => m.project_id === repJob) : []),
    [report, repJob]
  );
  const repMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
  const oh = report?.overhead;

  // ---- disperse overhead across jobs (Search page) ----
  // Each job's share of the range's (labour + materials) total decides its
  // share of the overhead: 50% of the work carries 50% of the overhead.
  const [dispersed, setDispersed] = useState(false);
  const grandJobs = report?.grand_total || 0;
  const ohShare = (jobTotal) => (dispersed && grandJobs > 0 && oh ? (oh.total * jobTotal) / grandJobs : 0);
  const ohSharePct = (jobTotal) => (grandJobs > 0 ? (jobTotal / grandJobs) * 100 : 0);

  // ---- fixed monthly overhead items manager (Search page only) ----
  const [ohManage, setOhManage] = useState(false);
  const [ohItems, setOhItems] = useState(null);
  const [ohDraft, setOhDraft] = useState({}); // item_id -> edited fields
  const [ohNew, setOhNew] = useState({ name: "", day_of_month: "", amount: "" });
  const [ohBusy, setOhBusy] = useState(false);

  const loadOhItems = () => api.get("/overhead/recurring")
    .then((r) => { setOhItems(r.data.items || []); setOhDraft({}); })
    .catch(() => toast.error("Could not load the monthly overhead items"));
  const refreshAll = () => { loadOhItems(); setRepNonce((n) => n + 1); };
  const ohErr = (e) => toast.error(e?.response?.data?.detail || "That monthly item could not be saved");
  const saveOhItem = async (it) => {
    const d = ohDraft[it.item_id];
    if (!d) return;
    setOhBusy(true);
    try {
      await api.put(`/overhead/recurring/${it.item_id}`, { ...it, ...d });
      refreshAll();
    } catch (e) { ohErr(e); } finally { setOhBusy(false); }
  };
  const delOhItem = async (it) => {
    if (!window.confirm(`Remove "${it.name}" from the monthly overhead items?`)) return;
    setOhBusy(true);
    try {
      await api.delete(`/overhead/recurring/${it.item_id}`);
      refreshAll();
    } catch (e) { ohErr(e); } finally { setOhBusy(false); }
  };
  const addOhItem = async () => {
    setOhBusy(true);
    try {
      await api.post("/overhead/recurring", {
        name: ohNew.name, day_of_month: Number(ohNew.day_of_month), amount: Number(ohNew.amount),
      });
      setOhNew({ name: "", day_of_month: "", amount: "" });
      refreshAll();
    } catch (e) { ohErr(e); } finally { setOhBusy(false); }
  };

  const ohInput = "rounded-md border border-white/15 bg-white/5 px-2 py-1 text-xs text-white focus:border-[#f97316] focus:outline-none";

  return (
    <div className="rounded-lg border border-white/15 bg-white/[0.04] p-4" data-testid="labour-report">
      <div className="mb-3 flex flex-wrap items-end gap-3">
        <div>
          <label htmlFor="labour-from" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">From</label>
          <input id="labour-from" type="date" value={repFrom} onChange={(e) => setRepFrom(e.target.value)} data-testid="labour-from"
            className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs text-white [color-scheme:dark] focus:border-[#f97316] focus:outline-none" />
        </div>
        <div>
          <label htmlFor="labour-to" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">To</label>
          <input id="labour-to" type="date" value={repTo} onChange={(e) => setRepTo(e.target.value)} data-testid="labour-to"
            className="rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs text-white [color-scheme:dark] focus:border-[#f97316] focus:outline-none" />
        </div>
        <div className="min-w-56">
          <label htmlFor="labour-job-select" className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-white/50">Job</label>
          <select id="labour-job-select" value={repJob} onChange={(e) => setRepJob(e.target.value)} data-testid="labour-job-select"
            className="w-full rounded-md border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs text-white [color-scheme:dark] focus:border-[#f97316] focus:outline-none">
            <option value="">All jobs</option>
            {(report?.jobs || []).map((j) => <option key={j.project_id} value={j.project_id}>{j.name}</option>)}
          </select>
        </div>
        {reportBusy && <span className="pb-1.5 text-[11px] text-white/40" data-testid="labour-loading">Loading…</span>}
        {report && !reportBusy && (
          <span className="pb-1.5 font-mono text-[11px] text-white/50" data-testid="labour-range-summary">
            {report.total_hours.toFixed(2)} hrs · {repMoney(report.grand_total)} across {report.jobs.length} job{report.jobs.length === 1 ? "" : "s"}
          </span>
        )}
      </div>

      {showOverhead && oh && (
        <div className="mb-3 rounded-lg border border-[#f97316]/40 bg-[#f97316]/10 p-4" data-testid="overhead-card">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#fb923c]">Overhead — gross pay and fixed costs not linked to a job</span>
            <span className="flex items-center gap-2">
              <span className="font-mono text-2xl font-bold text-[#fb923c]" data-testid="overhead-total">{repMoney(oh.total)}</span>
              <button
                onClick={() => setDispersed((v) => !v)}
                disabled={grandJobs <= 0}
                title={grandJobs <= 0 ? "No job costs in this range to spread the overhead across" : "Spread the overhead across the jobs below, proportional to each job's share of labour + materials"}
                className={`rounded-md border px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest disabled:opacity-30 ${
                  dispersed
                    ? "border-[#fb923c] bg-[#fb923c] text-[#1a1005]"
                    : "border-[#f97316]/50 text-[#fb923c] hover:bg-[#f97316]/20"
                }`}
                data-testid="overhead-disperse-btn"
              >
                {dispersed ? "Remove" : "Add"}
              </button>
            </span>
          </div>
          <p className="mt-1 font-mono text-[11px] leading-relaxed text-white/50" data-testid="overhead-breakdown">
            Unassigned {repMoney(oh.unassigned)} · Office {repMoney(oh.office)} · Estimating {repMoney(oh.estimating)}
            {" · "}Salary {repMoney(oh.salary)}
            {oh.salary_employees.length > 0 && ` (${oh.salary_employees.map((s) => s.name).join(", ")} — weekly ÷ 7 × ${oh.days} days)`}
            {" · "}Monthly items {repMoney(oh.recurring_total || 0)}
          </p>

          {/* the fixed monthly items whose date lands inside the range */}
          {(oh.recurring || []).length > 0 && (
            <div className="mt-2 overflow-hidden rounded-md border border-white/10" data-testid="overhead-recurring-list">
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="bg-white/5 text-left text-white/50">
                    <th className="px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest">Date</th>
                    <th className="px-2.5 py-1 font-mono text-[9px] uppercase tracking-widest">Monthly item</th>
                    <th className="w-24 px-2.5 py-1 text-right font-mono text-[9px] uppercase tracking-widest">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {oh.recurring.map((r, i) => (
                    <tr key={`${r.item_id}-${r.date}-${i}`} className="border-t border-white/10" data-testid={`overhead-recurring-${r.date}-${r.item_id}`}>
                      <td className="px-2.5 py-1 font-mono text-white/60">{r.date}</td>
                      <td className="px-2.5 py-1 text-white/85">{r.name}</td>
                      <td className="px-2.5 py-1 text-right font-mono text-white/85">{repMoney(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(oh.recurring || []).length === 0 && (
            <p className="mt-1.5 font-mono text-[10px] text-white/35" data-testid="overhead-recurring-none">No fixed monthly items fall inside this range.</p>
          )}

          <div className="mt-2">
            <button
              onClick={() => { const next = !ohManage; setOhManage(next); if (next) loadOhItems(); }}
              className="font-mono text-[10px] uppercase tracking-widest text-white/40 underline decoration-dotted underline-offset-4 hover:text-[#fb923c]"
              data-testid="overhead-manage-toggle"
              aria-expanded={ohManage}
            >
              {ohManage ? "Close monthly items" : "Manage monthly items"}
            </button>
          </div>

          {ohManage && (
            <div className="mt-2 space-y-1.5 rounded-md border border-white/10 p-2.5" data-testid="overhead-items-editor">
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                Fixed monthly costs — each applies on its day-of-month whenever that date is inside the range
              </p>
              {(ohItems || []).map((it) => {
                const d = { ...it, ...(ohDraft[it.item_id] || {}) };
                return (
                  <div key={it.item_id} className="flex flex-wrap items-center gap-1.5" data-testid={`overhead-item-${it.item_id}`}>
                    <input value={d.name} onChange={(e) => setOhDraft((p) => ({ ...p, [it.item_id]: { ...p[it.item_id], name: e.target.value } }))}
                      className={`${ohInput} min-w-44 flex-1`} data-testid={`overhead-item-name-${it.item_id}`} aria-label="Item name" />
                    <input type="number" min="1" max="31" value={d.day_of_month}
                      onChange={(e) => setOhDraft((p) => ({ ...p, [it.item_id]: { ...p[it.item_id], day_of_month: e.target.value } }))}
                      className={`${ohInput} w-16`} data-testid={`overhead-item-day-${it.item_id}`} aria-label="Day of month" title="Day of month" />
                    <input type="number" min="0" step="0.01" value={d.amount}
                      onChange={(e) => setOhDraft((p) => ({ ...p, [it.item_id]: { ...p[it.item_id], amount: e.target.value } }))}
                      className={`${ohInput} w-24 text-right font-mono`} data-testid={`overhead-item-amount-${it.item_id}`} aria-label="Monthly amount" />
                    <button onClick={() => saveOhItem(it)} disabled={ohBusy || !ohDraft[it.item_id]}
                      className="rounded-md border border-emerald-400/30 px-2 py-1 text-[10px] font-semibold uppercase text-emerald-300 hover:bg-emerald-400/10 disabled:opacity-30"
                      data-testid={`overhead-item-save-${it.item_id}`}>Save</button>
                    <button onClick={() => delOhItem(it)} disabled={ohBusy}
                      className="rounded-md border border-red-400/30 px-2 py-1 text-[10px] font-semibold uppercase text-red-300 hover:bg-red-400/10 disabled:opacity-30"
                      data-testid={`overhead-item-del-${it.item_id}`}>Delete</button>
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center gap-1.5 border-t border-white/10 pt-1.5">
                <input value={ohNew.name} onChange={(e) => setOhNew((p) => ({ ...p, name: e.target.value }))}
                  placeholder="New item (e.g. Truck payment)" className={`${ohInput} min-w-44 flex-1`} data-testid="overhead-add-name" aria-label="New item name" />
                <input type="number" min="1" max="31" value={ohNew.day_of_month} onChange={(e) => setOhNew((p) => ({ ...p, day_of_month: e.target.value }))}
                  placeholder="Day" className={`${ohInput} w-16`} data-testid="overhead-add-day" aria-label="New item day of month" title="Day of month" />
                <input type="number" min="0" step="0.01" value={ohNew.amount} onChange={(e) => setOhNew((p) => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00" className={`${ohInput} w-24 text-right font-mono`} data-testid="overhead-add-amount" aria-label="New item amount" />
                <button onClick={addOhItem} disabled={ohBusy || !ohNew.name.trim() || !ohNew.day_of_month || !ohNew.amount}
                  className="rounded-md bg-[#f97316] px-2.5 py-1 text-[10px] font-semibold uppercase text-white hover:bg-[#ea6a0a] disabled:opacity-40"
                  data-testid="overhead-add-btn">Add</button>
              </div>
            </div>
          )}
        </div>
      )}

      {report && report.jobs.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="labour-jobs-table">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="bg-[#1a3fd1] text-left text-white">
                <th className="px-3 py-2 font-semibold">Job</th>
                <th className="w-28 px-3 py-2 text-right font-semibold">Hours</th>
                <th className="w-32 px-3 py-2 text-right font-semibold">Gross labour</th>
                <th className="w-32 px-3 py-2 text-right font-semibold">Materials</th>
                <th className="w-32 px-3 py-2 text-right font-semibold">Job total</th>
                {dispersed && <th className="w-40 px-3 py-2 text-right font-semibold">Overhead share</th>}
              </tr>
            </thead>
            <tbody>
              {report.jobs.map((j) => (
                <tr key={j.project_id} onClick={() => setRepJob(repJob === j.project_id ? "" : j.project_id)}
                  className={`cursor-pointer border-t border-white/10 ${repJob === j.project_id ? "bg-[#f97316]/15" : "hover:bg-white/5"}`}
                  data-testid={`labour-job-row-${j.project_id}`}>
                  <td className="px-3 py-2 text-white/90">{j.name}</td>
                  <td className="px-3 py-2 text-right font-mono text-white/80" data-testid={`labour-job-hours-${j.project_id}`}>{j.hours.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono text-white/80" data-testid={`labour-job-cost-${j.project_id}`}>{repMoney(j.cost)}</td>
                  <td className="px-3 py-2 text-right font-mono text-white/80" data-testid={`labour-job-materials-${j.project_id}`}>{j.materials ? repMoney(j.materials) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-white" data-testid={`labour-job-total-${j.project_id}`}>{repMoney(j.total)}</td>
                  {dispersed && (
                    <td className="px-3 py-2 text-right" data-testid={`labour-job-overhead-${j.project_id}`}>
                      <span className="inline-block rounded-full border border-[#f97316]/40 bg-[#f97316]/15 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-[#fb923c]">
                        {repMoney(ohShare(j.total))} · {ohSharePct(j.total).toFixed(1)}%
                      </span>
                    </td>
                  )}
                </tr>
              ))}
              <tr className="border-t-2 border-white/25 bg-white/5" data-testid="labour-grand-total-row">
                <td className="px-3 py-2 font-semibold text-white">Total</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-white">{report.total_hours.toFixed(2)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-white" data-testid="labour-grand-labour">{repMoney(report.total_cost)}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold text-white" data-testid="labour-grand-materials">{repMoney(report.total_materials)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-[#fb923c]" data-testid="labour-grand-total">{repMoney(report.grand_total)}</td>
                {dispersed && (
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#fb923c]" data-testid="labour-grand-overhead">{repMoney(oh.total)}</td>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {report && report.jobs.length === 0 && !reportBusy && (
        <p className="text-xs text-white/40" data-testid="labour-empty">No labour or materials assigned to any job in this date range.</p>
      )}

      {repJob && (
        <div className="mt-3" data-testid="labour-entries">
          <p className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-white/50">
            {repJobRow?.name || "Selected job"} — every labour entry in range
          </p>
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="w-full min-w-[640px] border-collapse text-sm" data-testid="labour-entries-table">
              <thead>
                <tr className="bg-white/10 text-left text-white/80">
                  <th className="px-3 py-1.5 font-semibold">Date</th>
                  <th className="px-3 py-1.5 font-semibold">Employee</th>
                  <th className="w-24 px-3 py-1.5 text-right font-semibold">Hours</th>
                  <th className="w-24 px-3 py-1.5 text-right font-semibold">Rate</th>
                  <th className="w-28 px-3 py-1.5 text-right font-semibold">Total</th>
                  <th className="w-24 px-3 py-1.5 text-right font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {repEntries.map((e) => (
                  <tr key={e.entry_id} className="border-t border-white/10" data-testid={`labour-entry-${e.entry_id}`}>
                    <td className="px-3 py-1.5 font-mono text-white/70">{e.date}</td>
                    <td className="px-3 py-1.5 text-white/90">{e.employee}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-white/80">{e.hours.toFixed(2)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-white/60">{repMoney(e.rate)}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-white">{repMoney(e.total)}</td>
                    <td className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase">
                      {e.billed ? <span className="text-emerald-400">Billed</span> : <span className="text-amber-300">Unbilled</span>}
                    </td>
                  </tr>
                ))}
                {repEntries.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-xs text-white/40">No labour entries for this job in the range — materials only.</td></tr>
                )}
                <tr className="border-t-2 border-white/25 bg-white/5">
                  <td colSpan={4} className="px-3 py-1.5 font-semibold text-white">Gross labour</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold text-white" data-testid="labour-selected-total">
                    {repMoney(repJobRow?.cost ?? repEntries.reduce((s, e) => s + e.total, 0))}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mb-1.5 mt-4 font-mono text-[10px] uppercase tracking-widest text-white/50">
            Materials added in range
          </p>
          {repMats.length === 0 ? (
            <p className="text-xs text-white/40" data-testid="materials-empty">No materials logged against this job in the selected range.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full min-w-[560px] border-collapse text-sm" data-testid="materials-entries-table">
                <thead>
                  <tr className="bg-white/10 text-left text-white/80">
                    <th className="px-3 py-1.5 font-semibold">Date</th>
                    <th className="px-3 py-1.5 font-semibold">Vendor</th>
                    <th className="px-3 py-1.5 font-semibold">Description</th>
                    <th className="w-28 px-3 py-1.5 text-right font-semibold">Amount</th>
                    <th className="w-24 px-3 py-1.5 text-right font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {repMats.map((m) => (
                    <tr key={m.expense_id} className="border-t border-white/10" data-testid={`materials-entry-${m.expense_id}`}>
                      <td className="px-3 py-1.5 font-mono text-white/70">{m.date}</td>
                      <td className="px-3 py-1.5 text-white/90">{m.vendor || "—"}</td>
                      <td className="px-3 py-1.5 text-white/70">{m.description || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono text-white">{repMoney(m.amount)}</td>
                      <td className="px-3 py-1.5 text-right text-[10px] font-semibold uppercase">
                        {m.billed ? <span className="text-emerald-400">Billed</span> : <span className="text-amber-300">Unbilled</span>}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-white/25 bg-white/5">
                    <td colSpan={3} className="px-3 py-1.5 font-semibold text-white">Materials total</td>
                    <td className="px-3 py-1.5 text-right font-mono font-bold text-white" data-testid="materials-selected-total">
                      {repMoney(repJobRow?.materials ?? repMats.reduce((s, m) => s + m.amount, 0))}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 flex justify-end">
            <div className="rounded-lg border border-[#f97316]/40 bg-[#f97316]/10 px-4 py-2 text-sm">
              <span className="font-mono text-[10px] uppercase tracking-widest text-[#fb923c]">Job total (labour + materials)</span>
              <span className="ml-3 font-mono font-bold text-[#fb923c]" data-testid="labour-selected-grand">
                {repMoney(repJobRow?.total ?? 0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Role gate — single chokepoint covering both the Hours "Labour by job"
// toggle and the Search page. Labour costs are owner/superadmin/admin only;
// the backend returns 403 for other roles, this just keeps the panel (and
// its API call + error toast) from rendering at all.
export default function LabourReportPanel(props) {
  const { user } = useAuth();
  if (!LABOUR_REPORT_ROLES.includes(user?.role)) return null;
  return <LabourReportPanelInner {...props} />;
}
